/**
 * The cart's totals, priced by the server (T-130 / T-135).
 *
 * ── Why the widget cannot add up its own cart ───────────────────────────────
 *
 * It was summing unit prices. That number is right only when nothing else
 * applies, and three things routinely do:
 *
 *   - POSTAGE. A physical item's shipping is not in its price, so a printed
 *     product showed a total the buyer would never be charged.
 *   - MEMBER PRICING. A signed-in member sees a lower price than the list one,
 *     resolved server-side from their segments. The widget showed list price
 *     with no discount and no savings line.
 *   - REFUSALS. Some carts cannot be sold at all — a seller with no country
 *     cannot post anything, and the quote answers 422 SELLER_COUNTRY_UNKNOWN.
 *     Summing locally, the widget showed a confident total for an order that
 *     would be rejected at payment, which is the worst moment to find out.
 *
 * The quote endpoint is the same one the app's own cart and review step use,
 * so all three surfaces now agree about money. It is read-only — no Stripe
 * session, no rows written — and documented as safe to call on every render.
 */

export interface QuoteLine {
    productId: string;
    name: string;
    tierName: string | null;
    unitPrice: number;
    quantity: number;
    lineTotal: number;
    currency: string;
    /** Set when a member rate applied; the price BEFORE the discount. */
    listPrice: number | null;
}

export interface Quote {
    lines: QuoteLine[];
    subtotal: number;
    shipping: number;
    total: number;
    currency: string;
    memberSavings: number;
    requiresShipping: boolean;
}

export interface QuoteRefusal {
    /** e.g. SELLER_COUNTRY_UNKNOWN, PHYSICAL_CART_MUST_BE_ALONE. */
    code: string | null;
    message: string;
}

export interface QuoteRequestItem {
    productId: string;
    quantity: number;
}

/**
 * Ask the server what this cart costs.
 *
 * Returns `{ quote }` on success, `{ refusal }` when the server declines to
 * price it (422 — a real answer, not an error), and `null` when the call
 * itself failed. The caller must tell those apart: a refusal is something to
 * SHOW the buyer, a failure is something to fall back from, and treating a
 * network blip as a refusal would tell someone their order is impossible
 * because their wifi dropped.
 */
export async function fetchQuote(
    apiUrl: string,
    communityTag: string,
    items: QuoteRequestItem[],
    opts: { sameOrigin: boolean },
): Promise<{ quote?: Quote; refusal?: QuoteRefusal } | null> {
    if (items.length === 0) return null;

    /*
     * Same-origin goes through a RELATIVE path so the auth cookie rides along
     * and a signed-in member gets member pricing. The cookie is host-scoped,
     * so cross-origin it would not be sent anyway — there the call is
     * explicitly anonymous, and the buyer gets list pricing until they reach
     * the app. Asking for credentials cross-origin would only force a stricter
     * preflight for a cookie that cannot travel.
     */
    const url = opts.sameOrigin
        ? "/api/users/me/checkout/quote"
        : `${apiUrl}/api/users/me/checkout/quote`;

    try {
        const res = await fetch(url, {
            method: "POST",
            credentials: opts.sameOrigin ? "include" : "omit",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ items, communityTag }),
        });

        if (res.status === 422) {
            const body = await res.json().catch(() => ({}));
            return {
                refusal: {
                    code: body?.code ?? null,
                    message: body?.error || "This order cannot be completed right now.",
                },
            };
        }
        if (!res.ok) return null;
        return { quote: (await res.json()) as Quote };
    } catch {
        return null;
    }
}
